import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from '@legendapp/state/react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as FileSystem from 'expo-file-system/legacy';
import { v4 as uuidv4 } from 'uuid';
import theme from '@/config/theme';
import { globalState } from '@/state/globalState';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { getLogger } from '@/utils/logger';
import { APP_CONSTANTS } from '@/config/constants';
import { databaseService } from '@/services/databaseService';
import {
  uploadImageHelper,
  processPendingImages,
  PendingProcessResult,
} from '@/utils/file/uploadImageHelper';
import { ensureDirectoryExists } from '@/utils/file/fileHelpers';
import { generateTestJpegBase64 } from '@/utils/file/sampleImageGenerator';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';

const logger = getLogger('DEV-TEST-409');
const { TABLES } = APP_CONSTANTS.DATABASE;

const ActionButton = ({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) => (
  <TouchableOpacity
    style={[styles.actionButton, disabled && styles.actionButtonDisabled]}
    onPress={disabled ? undefined : onPress}
    activeOpacity={disabled ? 1 : 0.8}
  >
    <Text style={styles.actionButtonLabel}>{label}</Text>
  </TouchableOpacity>
);

const DevTest409Screen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  const isDeveloper = hasDeveloperBypass(profile?.user_type);
  const defaultEmpresaId =
    company?.id || profile?.empresa_id || '049cf689-84f1-46f5-9460-26def21dce59';

  const [empresaId, setEmpresaId] = useState(defaultEmpresaId);
  const [checklistId, setChecklistId] = useState(uuidv4());
  const [campoId, setCampoId] = useState('1d94f2e0-e49f-4cf7-afbc-9b416c224982');

  const [localUri, setLocalUri] = useState<string | null>(null);
  const [relativePath, setRelativePath] = useState<string | null>(null);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [processResult, setProcessResult] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [isBusy, setIsBusy] = useState(false);

  const bucketChecklist = APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST;

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 200));
  }, []);

  const refreshPendingCount = useCallback(async () => {
    try {
      const entries = await databaseService.listItems(
        TABLES.PENDING_IMAGES.NAME,
        { item_id: checklistId, item_type: 'CHECKLIST' }
      );
      setPendingCount(entries.length);
    } catch (error) {
      logger.error('[DEV-TEST-409] Falha ao contar pendências', error);
    }
  }, [checklistId]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  const ensureChecklistExists = useCallback(async () => {
    if (!empresaId) {
      addLog('Informe uma empresa antes de criar o checklist fake.');
      return;
    }
    const checklistTable = TABLES.GUINCHO_CHECKLISTS.NAME;
    const existing = await databaseService.readItem(checklistTable, checklistId);
    if (existing) {
      addLog(`Checklist ${checklistId} já existe no banco local.`);
      return;
    }
    const now = Date.now();
    await databaseService.createItem(checklistTable, {
      id: checklistId,
      template_id: 'e8e94cd1-364b-4a7b-91ed-5edbdf898af9',
      versao: 1,
      empresa_id: empresaId,
      usuario_id: profile?.id ?? null,
      data_criacao: now,
      status: 'in_progress',
      created_at: now,
      updated_at: now,
    });
    addLog(`Checklist fake ${checklistId} criado com sucesso.`);
  }, [empresaId, checklistId, profile?.id, addLog]);

  const regenerateChecklistId = useCallback(() => {
    const nextId = uuidv4();
    setChecklistId(nextId);
    setLocalUri(null);
    setRelativePath(null);
    setLastUrl(null);
    addLog(`Novo checklist_id gerado: ${nextId}`);
  }, [addLog]);

  const writeLocalImage = useCallback(
    async (reusePath = false) => {
      if (!empresaId) {
        addLog('Informe uma empresa antes de gerar o arquivo.');
        return;
      }
      const base64Payload = generateTestJpegBase64();
      const targetRelative =
        reusePath && relativePath
          ? relativePath
          : `checklist-photos/${empresaId}/${checklistId}-${campoId}-${Date.now()}-dev409.jpg`;

      if (Platform.OS === 'web') {
        try {
          await databaseService.deleteItem(
            TABLES.CHECKLIST_PHOTOS.NAME,
            targetRelative,
            true
          );
        } catch {
          // ignorar caso não exista
        }
        await databaseService.createItem(
          TABLES.CHECKLIST_PHOTOS.NAME,
          {
            id: targetRelative,
            base64: base64Payload,
            created_at: Date.now(),
            updated_at: Date.now(),
          },
          true
        );
        setLocalUri(targetRelative);
      } else {
        const fileUri = `${FileSystem.documentDirectory}${targetRelative}`;
        const directory = fileUri.substring(0, fileUri.lastIndexOf('/') + 1);
        await ensureDirectoryExists(directory);
        await FileSystem.writeAsStringAsync(fileUri, base64Payload, {
          encoding: FileSystem.EncodingType.Base64,
        });
        setLocalUri(fileUri);
      }

      setRelativePath(targetRelative);
      addLog(
        `Arquivo local preparado em ${Platform.OS === 'web' ? 'IndexedDB' : 'FileSystem'} (${targetRelative}).`
      );
    },
    [empresaId, checklistId, campoId, relativePath, addLog]
  );

  const handleUpload = useCallback(async () => {
    if (!localUri) {
      addLog('Nenhum arquivo local disponível. Gere o arquivo antes de enviar.');
      return;
    }
    setIsBusy(true);
    try {
      const result = await uploadImageHelper({
        localUri,
        itemType: 'CHECKLIST',
        itemId: checklistId,
        campoId,
      });
      setLastUrl(result.publicURL);
      addLog(`Upload finalizado. URL pública: ${result.publicURL}`);
    } catch (error: any) {
      const message = error?.message || 'Erro desconhecido no upload.';
      addLog(`Falha no upload: ${message}`);
    } finally {
      setIsBusy(false);
      refreshPendingCount();
    }
  }, [localUri, checklistId, campoId, addLog, refreshPendingCount]);

  const handleRegisterPending = useCallback(async () => {
    if (!localUri || !relativePath) {
      addLog('É necessário gerar o arquivo local para registrar a pendência.');
      return;
    }
    const pendingTable = TABLES.PENDING_IMAGES.NAME;
    const existing = await databaseService.listItems<{ id: string }>(pendingTable, {
      item_id: checklistId,
      item_type: 'CHECKLIST',
      campo_id: campoId,
    });
    const payload = {
      item_type: 'CHECKLIST',
      item_id: checklistId,
      local_uri: localUri,
      bucket: bucketChecklist,
      path: relativePath,
      column_name: null,
      campo_id: campoId,
      created_at: Date.now(),
    };
    if (existing.length > 0) {
      await databaseService.updateItem(pendingTable, existing[0].id, payload, true);
      addLog('Pendência existente atualizada com o novo arquivo local.');
    } else {
      await databaseService.createItem(
        pendingTable,
        { id: uuidv4(), ...payload },
        true
      );
      addLog('Pendência registrada manualmente com sucesso.');
    }
    refreshPendingCount();
  }, [localUri, relativePath, checklistId, campoId, bucketChecklist, refreshPendingCount, addLog]);

  const handleProcessPendings = useCallback(async () => {
    setIsBusy(true);
    setProcessResult(null);
    try {
      const result: PendingProcessResult = await processPendingImages();
      setProcessResult(
        `Total: ${result.total} • Processadas: ${result.processed} • Sucesso: ${result.success} • Erros: ${result.errors}`
      );
      addLog('processPendingImages executado. Consulte o console para detalhes.');
    } catch (error: any) {
      const message = error?.message || 'Erro ao processar pendências.';
      setProcessResult(`Falha: ${message}`);
      addLog(`Erro ao processar pendências: ${message}`);
    } finally {
      setIsBusy(false);
      refreshPendingCount();
    }
  }, [refreshPendingCount, addLog]);

  const developerOnly = useMemo(() => {
    if (isDeveloper) return null;
    return (
      <View style={styles.restrictedContainer}>
        <Text style={styles.restrictedText}>
          Apenas usuários com perfil de desenvolvedor podem acessar este laboratório.
        </Text>
        <TouchableOpacity style={styles.backButtonAlt} onPress={() => router.back()}>
          <MaterialCommunityIcons name="arrow-left" size={20} color="#fff" />
          <Text style={styles.backButtonAltText}>Voltar</Text>
        </TouchableOpacity>
      </View>
    );
  }, [isDeveloper, router]);

  if (!isDeveloper) {
    return developerOnly;
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Teste de upload 409</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.helperText}>
          Utilize os botões abaixo para simular o fluxo de upload/pendência e observar o tratamento do status 409 (arquivo já existente).
        </Text>

        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Empresa</Text>
          <TextInput
            style={styles.input}
            value={empresaId}
            placeholder="empresa_id"
            onChangeText={setEmpresaId}
          />
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Checklist</Text>
          <View style={styles.inlineField}>
            <TextInput
              style={[styles.input, { flex: 1 }]}
              value={checklistId}
              placeholder="checklist_id"
              onChangeText={setChecklistId}
              autoCapitalize="none"
            />
            <TouchableOpacity style={styles.inlineButton} onPress={regenerateChecklistId}>
              <MaterialCommunityIcons name="refresh" size={18} color={theme.colors.text.inverse} />
              <Text style={styles.inlineButtonText}>Gerar</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Campo</Text>
          <TextInput
            style={styles.input}
            value={campoId}
            placeholder="campo_id"
            onChangeText={setCampoId}
          />
        </View>

        <View style={styles.actionsRow}>
          <ActionButton label="1) Criar checklist fake" onPress={ensureChecklistExists} />
          <ActionButton label="2) Gerar arquivo novo" onPress={() => writeLocalImage(false)} />
          <ActionButton
            label="2b) Reusar último path"
            onPress={() => writeLocalImage(true)}
            disabled={!relativePath}
          />
        </View>

        <View style={styles.actionsRow}>
          <ActionButton
            label="3) Upload via helper"
            onPress={handleUpload}
            disabled={isBusy || !localUri}
          />
          <ActionButton
            label="4) Registrar pendência"
            onPress={handleRegisterPending}
            disabled={!localUri || !relativePath}
          />
          <ActionButton
            label="5) processPendingImages"
            onPress={handleProcessPendings}
            disabled={isBusy}
          />
        </View>

        <View style={styles.statusCard}>
          <Text style={styles.statusTitle}>Estado atual</Text>
          <Text style={styles.statusItem}>Local URI: {localUri || '—'}</Text>
          <Text style={styles.statusItem}>Path relativo: {relativePath || '—'}</Text>
          <Text style={styles.statusItem}>Última URL pública: {lastUrl || '—'}</Text>
          <Text style={styles.statusItem}>Pendências registradas: {pendingCount}</Text>
          <Text style={styles.statusItem}>
            Último processPendingImages: {processResult || '—'}
          </Text>
        </View>

        <View style={styles.logsCard}>
          <View style={styles.logsHeader}>
            <Text style={styles.sectionTitle}>Logs em memória</Text>
            <TouchableOpacity onPress={() => setLogs([])}>
              <MaterialCommunityIcons name="trash-can-outline" size={18} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          {logs.length === 0 ? (
            <Text style={styles.helperText}>Nenhuma ação registrada ainda.</Text>
          ) : (
            logs.map((entry, index) => (
              <Text key={`${entry}-${index}`} style={styles.logEntry}>
                {entry}
              </Text>
            ))
          )}
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    height: 56,
    backgroundColor: theme.colors.primary.default,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  headerRight: {
    width: 40,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.md,
    gap: theme.spacing.md,
  },
  helperText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  fieldGroup: {
    gap: 6,
  },
  inlineField: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'center',
  },
  inlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
  },
  inlineButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
  },
  label: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  input: {
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: Platform.OS === 'web' ? theme.spacing.sm : theme.spacing.xs,
    backgroundColor: theme.colors.background.primary,
    color: theme.colors.text.primary,
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  actionButton: {
    flexGrow: 1,
    minWidth: 120,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButtonDisabled: {
    backgroundColor: theme.colors.border.default,
  },
  actionButtonLabel: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
    textAlign: 'center',
  },
  statusCard: {
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    gap: 6,
  },
  statusTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  statusItem: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  logsCard: {
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    gap: 8,
  },
  logsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  logEntry: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  restrictedContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background.secondary,
  },
  restrictedText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    textAlign: 'center',
    marginBottom: theme.spacing.md,
  },
  backButtonAlt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
  },
  backButtonAltText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
  },
});

export default DevTest409Screen;
